import { ContactType, DoctorListDto, SubtenantDto } from '@lmt/shared-types';
import {
  Field,
  FieldLinkProps,
} from '@lmt/unicorn-shared/app/components/common/Field';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { DatePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/DatePicker';
import { ComboBoxInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/ComboBoxInput';
import {
  DropDownInput,
  HookFormDropDownInput,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { getDoctorFullName } from '@lmt/unicorn-shared/app/components/doctors/doctor-form/common/common';
import { minBirthDate } from '@lmt/unicorn-shared/app/components/patients/common';
import React, { FC } from 'react';
import { Controller } from 'react-hook-form';
import { UseFormReturn } from 'react-hook-form/dist/types';
import { validateAndDirty } from '@lmt/unicorn-shared/app/helpers/form-helper';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import { sexTypes, titleKey } from '../common/common';
import { PatientData } from '../common/types';
import { PriorityBadge } from './PriorityBadge';
import { localizeSexType } from '@lmt/unicorn-shared/app/helpers/localization/translations';
import {
  useScopedTranslation,
  NS_DataBlockFields,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import styles from './PatientMiniForm.module.scss';
import { useTranslation } from 'react-i18next';
import {
  CustomDictionaryName,
  useActiveDictionaryValuesByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';

interface PatientFormProps {
  form: UseFormReturn<PatientData>;
  isSubtenantEnabled: boolean;
  doctors: DoctorListDto[];
  addNewDoctor: () => void;
  subtenants: SubtenantDto[];
  onDoctorsSearch: (value: string | null) => void;
  isDoctorsLoading?: boolean;
}

const nameof = nameofFactory<PatientData>();

export const PatientMiniForm: FC<PatientFormProps> = (props) => {
  const {
    isSubtenantEnabled,
    doctors,
    subtenants,
    form,
    isDoctorsLoading,
    addNewDoctor,
  } = props;
  const { formState, register, setValue, getValues, control, watch } = form;
  const i18next = useScopedTranslation('patients.form.fields');
  const i18nextDict = useTranslation(NS_DataBlockFields);
  const claims = useClaims();

  const customValues = watch('values');

  const { data: personTitles } = useActiveDictionaryValuesByTitle(
    CustomDictionaryName.PersonTitles,
    customValues[titleKey],
  );

  const priorityContactType = watch(nameof('priorityContactType'));

  function changePriorityContactType(contactType: ContactType) {
    setValue(nameof('priorityContactType'), contactType, validateAndDirty);
  }
  function getLinkProps(
    contactType: ContactType,
    field: keyof PatientData,
  ): FieldLinkProps | undefined {
    if (priorityContactType === contactType) {
      return {
        title: <PriorityBadge />,
      };
    }
    const contact = getValues(nameof(field));
    if (!contact?.trim()) {
      return undefined;
    }

    return {
      title: i18next.t('operations.make_priority'),
      className: styles.link,
      onClick: () => changePriorityContactType(contactType),
    };
  }

  return (
    <div className={styles.dataContainer} data-test-id="patientMiniForm">
      <Field
        className={styles.firstField}
        title={i18next.t('first_name')}
        required={true}
      >
        <Input
          {...register('firstName')}
          errorText={formState.errors.firstName?.message}
          testId="firstName"
        />
      </Field>
      <Field
        className={styles.field}
        title={i18next.t('last_name')}
        required={true}
      >
        <Input
          {...register('lastName')}
          errorText={formState.errors.lastName?.message}
          testId="lastName"
        />
      </Field>
      <div className={styles.row}>
        <Field className={styles.smallField} title={i18next.t('sex')}>
          <Controller
            control={control}
            name={'sex'}
            render={({ field: { onChange, value } }) => (
              <DropDownInput
                required={false}
                useIdFunctionAsValue={true}
                variant="formInput"
                getOptionLabel="displayValue"
                idFunction="value"
                errorText={formState.errors.sex?.message}
                options={sexTypes.map((sexType) => ({
                  value: sexType,
                  displayValue: localizeSexType(sexType),
                }))}
                value={value}
                onValueChanged={(option) => {
                  onChange(option?.value ?? null);
                }}
                testId="sexDropDown"
              />
            )}
          />
        </Field>
        <Field className={styles.smallField} title={i18next.t('title')}>
          <ComboBoxInput
            required={false}
            variant={'formInput'}
            options={personTitles}
            value={
              personTitles.find((x) => x.id === customValues[titleKey]) ??
              customValues[titleKey]
            }
            onValueChanged={(customValue, item) => {
              if (item) {
                customValues[titleKey] = item.id;
              } else {
                customValues[titleKey] = customValue;
              }
              setValue(nameof('values'), customValues, validateAndDirty);
            }}
            labelFunction={(item) => {
              return item.value ? i18nextDict.t(item.value) : item;
            }}
            testId="patient-title"
          />
        </Field>
        <Field
          className={styles.smallField}
          title={i18next.t('date_of_birth')}
          required={true}
        >
          <Controller
            name={'dateOfBirth'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                error={!!formState.errors.dateOfBirth}
                errorText={(formState.errors.dateOfBirth as any)?.message}
                value={value}
                minDate={minBirthDate}
                onDateChanged={onChange}
                testId="dateOfBirth"
                allowToSelectYear={true}
              />
            )}
          />
        </Field>
      </div>
      <Field
        className={styles.field}
        title={i18next.t('mobile_phone')}
        linkProps={getLinkProps(ContactType.MobilePhone, 'mobilePhone')}
      >
        <Input
          {...register('mobilePhone')}
          errorText={
            formState.errors.mobilePhone?.message ||
            formState.errors.contact?.message ||
            formState.errors.priorityContactType?.message ||
            formState.errors.priorityContact?.message
          }
        />
      </Field>
      <Field
        className={styles.field}
        title={i18next.t('email')}
        linkProps={getLinkProps(ContactType.Email, 'email')}
      >
        <Input
          {...register('email')}
          errorText={
            formState.errors.email?.message ||
            formState.errors.contact?.message ||
            formState.errors.priorityContactType?.message ||
            formState.errors.priorityContact?.message
          }
        />
      </Field>
      <Field title={''}>
        <Controller
          /*Using a controller here because from checkboxes break
                when name is not unique in the whole DOM tree*/
          control={form.control}
          name={'emailsAllowed'}
          render={({ field: { onChange, value } }) => (
            <CheckBox
              className={styles.checkbox}
              title={i18next.t('emails_allowed')}
              onChange={onChange}
              checked={value ?? undefined}
              testId="emailsAllowed"
            />
          )}
        />
      </Field>
      <div className={styles.row}>
        {isSubtenantEnabled && (
          <Field
            className={styles.middleField}
            title={i18next.t('subtenant')}
            required={true}
          >
            <Controller
              control={control}
              name={'subtenantId'}
              render={({ field: { onChange, value } }) => (
                <DropDownInput
                  useIdFunctionAsValue={true}
                  required
                  variant="formInput"
                  getOptionLabel="name"
                  idFunction="id"
                  errorText={formState.errors.subtenantId?.message}
                  options={subtenants}
                  value={value}
                  onValueChanged={(subtenant) => {
                    onChange(subtenant.id);
                  }}
                  testId="subtenant"
                />
              )}
            />
          </Field>
        )}
        <Field
          className={isSubtenantEnabled ? styles.middleField : styles.field}
          title={i18next.t('doctor')}
          linkProps={
            claims.permissions.doctorManagement
              ? {
                  title: i18next.t('operations.add_new_doctor'),
                  onClick: () => addNewDoctor(),
                }
              : undefined
          }
        >
          <HookFormDropDownInput
            control={control}
            name={'doctorId'}
            required={false}
            useVirtualization={true}
            enableSearch={true}
            variant="formInput"
            getOptionLabel={getDoctorFullName}
            idFunction="id"
            options={doctors}
            testId="doctorDropDown"
            loading={isDoctorsLoading}
          />
        </Field>
      </div>
    </div>
  );
};
